import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Mocking and testing fetch requests with Jest",
  "description": "",
  "published": "2020-02-28T00:00:00.000Z",
  "redirect_from": ["/blog/mocking-and-testing-fetch-requests-with-jest/swizec/9338"],
  "categories": "Front End, Technical",
  "hero": "./img/techletter.app-screenshot-1582908304324.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Some days your code flows, your fingers fly, and you're god amongst nerds. Other days you're testing `}<inlineCode parentName="p">{`fetch()`}</inlineCode>{` requests.`}</p>
    <p>{`So I'm writing this to save you some time. The definitive `}<em parentName="p">{`how to mock and test fetch requests`}</em>{` guide for 2020.`}</p>
    <p>{`First, a little background`}</p>
    <h2 {...{
      "id": "why-even-test-requests"
    }}>{`Why even test requests?`}</h2>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/rauchg/status/807626710350839808"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "640px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "34.08071748878923%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'136\\'%20viewBox=\\'0%200%20400%20136\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M24%2016c-7%203-10%2012-7%2020%202%205%2010%2010%209%205%200-2%201-2%202-2%202%200%203%201%203%202h5c5-1%208-6%208-12-1-10-11-17-20-13m151%203c-2%202-2%203-2%205%201%204%201%204%205%205%204%200%207-3%207-7-2-5-7-6-10-3m-49%204c0%204%200%205%202%205l1-2v-2l2%202c1%201%203%202%207%202%205%200%205%200%205-3l-2-4c-2-1-5-1-5%201l-1%202c-1%202-1%202-1-3%200-2-1-3-4-3h-4v5\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/2bc86287dac9b671b5a8367957ca1f2b/ca0a1/techletter.app-screenshot-1582908304324.webp 223w", "/static/2bc86287dac9b671b5a8367957ca1f2b/75680/techletter.app-screenshot-1582908304324.webp 445w", "/static/2bc86287dac9b671b5a8367957ca1f2b/0ba47/techletter.app-screenshot-1582908304324.webp 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/2bc86287dac9b671b5a8367957ca1f2b/e92b6/techletter.app-screenshot-1582908304324.png 223w", "/static/2bc86287dac9b671b5a8367957ca1f2b/e66bf/techletter.app-screenshot-1582908304324.png 445w", "/static/2bc86287dac9b671b5a8367957ca1f2b/6af66/techletter.app-screenshot-1582908304324.png 640w"],
                "sizes": "(max-width: 640px) 100vw, 640px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/2bc86287dac9b671b5a8367957ca1f2b/6af66/techletter.app-screenshot-1582908304324.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Integration testing. Also known as the only useful testing. 😛`}</p>
    <p>{`Semantics aside, most bugs happen on the edges. The touch points between systems. Interfaces between modules.`}</p>
    <p>{`What bigger in-between point than calling an API? Lots can go wrong. Server can fail, network down, strange response codes, or just corruption on the wire.`}</p>
    <p>{`Take for example this `}<inlineCode parentName="p">{`isAuthenticated`}</inlineCode>{` method from a library I built at work.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=async%20isAuthenticated()%3A%20Promise%3Cboolean%3E%20%7B%0A%20%20%20%20const%20expiresAt%20%3D%20new%20Date(%0A%20%20%20%20%20%20%20%20%20%20%20%20Cookies.get(%60%24%7BCOOKIE_PREFIX%7D-expires-at%60)%20%7C%7C%200%0A%20%20%20%20%20%20%20%20)%2C%0A%20%20%20%20%20%20%20%20userId%20%3D%20this.getUserId()%3B%0A%0A%20%20%20%20if%20(!userId%20%7C%7C%20new%20Date()%20%3E%3D%20expiresAt)%20%7B%0A%20%20%20%20%20%20%20%20%2F%2F%20we%20locally%20know%20you%20aren't%20authenticated%0A%20%20%20%20%20%20%20%20return%20false%3B%0A%20%20%20%20%7D%0A%0A%20%20%20%20return%20this.authorizeWithServer()%3B%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "574px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "70.4035874439462%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'281\\'%20viewBox=\\'0%200%20400%20281\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M23%2035c-2%202-1%20210%201%20212s350%202%20352%200%203-210%201-212-352-2-354%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/5914f82bcf69624325e82b18c8d1062c/ca0a1/techletter.app-screenshot-1582908305341.webp 223w", "/static/5914f82bcf69624325e82b18c8d1062c/75680/techletter.app-screenshot-1582908305341.webp 445w", "/static/5914f82bcf69624325e82b18c8d1062c/4e18f/techletter.app-screenshot-1582908305341.webp 574w"],
                "sizes": "(max-width: 574px) 100vw, 574px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/5914f82bcf69624325e82b18c8d1062c/e92b6/techletter.app-screenshot-1582908305341.png 223w", "/static/5914f82bcf69624325e82b18c8d1062c/e66bf/techletter.app-screenshot-1582908305341.png 445w", "/static/5914f82bcf69624325e82b18c8d1062c/86389/techletter.app-screenshot-1582908305341.png 574w"],
                "sizes": "(max-width: 574px) 100vw, 574px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/5914f82bcf69624325e82b18c8d1062c/86389/techletter.app-screenshot-1582908305341.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Is the user authenticated? Check local cookies. That's always gonna work.`}</p>
    <p>{`And if cookies `}<em parentName="p">{`look`}</em>{` correct, you gotta ask the server if they're `}<em parentName="p">{`actually`}</em>{` correct. User might have been banned, deleted, access revoked, or just changed their password.`}</p>
    <p>{`That API request is the most likely failure point.`}</p>
    <p>{`Wanna `}<em parentName="p">{`really`}</em>{` test this method? Gonna have to test the fetch.`}</p>
    <h2 {...{
      "id": "why-mock-fetch-requests"
    }}>{`Why mock fetch requests?`}</h2>
    <p>{`A pure unit testing approach says you should never mock requests to other systems.`}</p>
    <p>{`You assume the request works and test that your function makes the request. Then you test the function that's called after the fetch.`}</p>
    <p>{`And you enter the unit testing fallacy.`}</p>
    <p><img parentName="p" {...{
        "src": "/cbba748a2d2adb477bb63217f1f9c733/media-Q0Mpd9MWuERyg-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`Besides, structuring your code to allow a pure unit testing approach makes it harder to read, understand, and tedious to work with. You'd need something like this:`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=private%20async%20authorizeWithServer()%20%7B%0A%20%20%20%20%2F%2F%20you%20*might*%20be%20authenticated%2C%20we%20have%20to%20check%20with%20the%20server%0A%20%20%20%20const%20response%20%3D%20fetch(%22localhost%3A3000%2Fapi%2Fuser%2Fauthorize%22)%3B%0A%20%20%20%20%0A%20%20%20%20this.parseAuthorizeWithServerResult(response)%0A%7D%0A%0Aprivate%20async%20parseAuthorizeWithServerResult(response)%20%7B%0A%20%20%20%20%2F%2F%20do%20stuff%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "654px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "53.36322869955157%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'213\\'%20viewBox=\\'0%200%20400%20213\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M20%2030a1907%201907%200%20000%20153c3%203%20357%203%20360%200l1-76-1-77c-1-2-359-2-360%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/f8c276ec355806b79287baa256f4cab3/ca0a1/techletter.app-screenshot-1582908305216.webp 223w", "/static/f8c276ec355806b79287baa256f4cab3/75680/techletter.app-screenshot-1582908305216.webp 445w", "/static/f8c276ec355806b79287baa256f4cab3/d7085/techletter.app-screenshot-1582908305216.webp 654w"],
                "sizes": "(max-width: 654px) 100vw, 654px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/f8c276ec355806b79287baa256f4cab3/e92b6/techletter.app-screenshot-1582908305216.png 223w", "/static/f8c276ec355806b79287baa256f4cab3/e66bf/techletter.app-screenshot-1582908305216.png 445w", "/static/f8c276ec355806b79287baa256f4cab3/68e9c/techletter.app-screenshot-1582908305216.png 654w"],
                "sizes": "(max-width: 654px) 100vw, 654px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/f8c276ec355806b79287baa256f4cab3/68e9c/techletter.app-screenshot-1582908305216.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Yeah that's not gonna get out of hand 🙄`}</p>
    <p>{`Keep your code simple and mock the request instead. Hook into the `}<inlineCode parentName="p">{`fetch()`}</inlineCode>{` API and manipulate what it returns. That is the way to testing bliss my friend.`}</p>
    <h2 {...{
      "id": "how-to-mock-fetch-requests-in-jest"
    }}>{`How to mock fetch requests in Jest`}</h2>
    <p>{`After more hours of trial and error than I dare admit, here's what I found: You'll need to mock the `}<inlineCode parentName="p">{`fetch`}</inlineCode>{` method itself, write a mock for each request, and use a fetch mocking library.`}</p>
    <h3 {...{
      "id": "install"
    }}>{`install`}</h3>
    <p>{`The library that worked best for me was `}<a parentName="p" {...{
        "href": "https://github.com/wheresrhys/fetch-mock"
      }}><inlineCode parentName="a">{`fetch-mock`}</inlineCode></a>{`.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=shell&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=yarn%20add%20--dev%20fetch-mock"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "302px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "61.88340807174888%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'248\\'%20viewBox=\\'0%200%20400%20248\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M45%2066l-2%203v56c0%2054%200%2057%202%2059s38%203%20156%203c117%200%20153-1%20155-3s2-5%202-59V68l-2-2-3-2H47l-2%202m47%2021c-4%204-3%207%201%2011s12%200%2012-6c0-7-9-10-13-5m29-2c-5%202-6%207-3%2011%205%207%2015%201%2013-7-1-2-6-5-7-5l-3%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d08fbb2aee04a7bf4cffb348d4b2e23f/ca0a1/techletter.app-screenshot-1582908305188.webp 223w", "/static/d08fbb2aee04a7bf4cffb348d4b2e23f/7cc02/techletter.app-screenshot-1582908305188.webp 302w"],
                "sizes": "(max-width: 302px) 100vw, 302px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d08fbb2aee04a7bf4cffb348d4b2e23f/e92b6/techletter.app-screenshot-1582908305188.png 223w", "/static/d08fbb2aee04a7bf4cffb348d4b2e23f/5f1d2/techletter.app-screenshot-1582908305188.png 302w"],
                "sizes": "(max-width: 302px) 100vw, 302px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/d08fbb2aee04a7bf4cffb348d4b2e23f/5f1d2/techletter.app-screenshot-1582908305188.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <h3 {...{
      "id": "mock"
    }}>{`mock`}</h3>
    <p>{`You tell Jest to use a mock library like this:`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20src%2F__mocks__%2Fisomorphic-fetch.js%0A%0Aconst%20fetchMock%20%3D%20require(%22fetch-mock%22)%3B%0A%0Aconst%20fetch%20%3D%20fetchMock.sandbox()%3B%0Amodule.exports%20%3D%20fetch%3B"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "422px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "65.47085201793722%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'263\\'%20viewBox=\\'0%200%20400%20263\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M32%2048c-2%201-3%20165-1%20168s15%203%20169%203%20167%200%20169-3%201-167-1-168c-2-3-334-3-336%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/5dd75644b977d3a6ec6b780b2eebc96d/ca0a1/techletter.app-screenshot-1582908305230.webp 223w", "/static/5dd75644b977d3a6ec6b780b2eebc96d/fc0b3/techletter.app-screenshot-1582908305230.webp 422w"],
                "sizes": "(max-width: 422px) 100vw, 422px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/5dd75644b977d3a6ec6b780b2eebc96d/e92b6/techletter.app-screenshot-1582908305230.png 223w", "/static/5dd75644b977d3a6ec6b780b2eebc96d/fa5c1/techletter.app-screenshot-1582908305230.png 422w"],
                "sizes": "(max-width: 422px) 100vw, 422px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/5dd75644b977d3a6ec6b780b2eebc96d/fa5c1/techletter.app-screenshot-1582908305230.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Jest imports this file instead of `}<inlineCode parentName="p">{`isomorphic-fetch`}</inlineCode>{` when running your code. Same approach works to replace any other library.`}</p>
    <p>{`Put a file of `}{`<`}{`library name> in `}<inlineCode parentName="p">{`src/__mocks__`}</inlineCode>{` and that file becomes said library. In this case we're replacing the `}<inlineCode parentName="p">{`isomorphic-fetch`}</inlineCode>{` library with a `}<inlineCode parentName="p">{`fetch-mock`}</inlineCode>{` sandbox.`}</p>
    <p>{`So when your code says`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=import%20fetch%20from%20'isomorphic-fetch'"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "390px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "47.98206278026906%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'192\\'%20viewBox=\\'0%200%20400%20192\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M35%2052c-2%202-2%208-2%2046%200%2042%200%2043%202%2045%203%203%20328%203%20331%200%202-2%202-3%202-45%200-38%200-44-2-46l-1-2H36l-1%202m36%2016c-2%203-2%204%200%207%203%204%2010%202%2010-4s-6-8-10-3m21%200c-3%202-3%204%200%207%205%205%2012-1%209-7-3-3-6-3-9%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/37b0335b51edc896663f607e5767f8cd/ca0a1/techletter.app-screenshot-1582908305371.webp 223w", "/static/37b0335b51edc896663f607e5767f8cd/a9b47/techletter.app-screenshot-1582908305371.webp 390w"],
                "sizes": "(max-width: 390px) 100vw, 390px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/37b0335b51edc896663f607e5767f8cd/e92b6/techletter.app-screenshot-1582908305371.png 223w", "/static/37b0335b51edc896663f607e5767f8cd/727ba/techletter.app-screenshot-1582908305371.png 390w"],
                "sizes": "(max-width: 390px) 100vw, 390px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/37b0335b51edc896663f607e5767f8cd/727ba/techletter.app-screenshot-1582908305371.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`It's actually getting the sandbox 🤘`}</p>
    <p>{`You'll need to import `}<em parentName="p">{`a`}</em>{` fetch to support mocking – can't rely on the global `}<inlineCode parentName="p">{`window.fetch`}</inlineCode>{` I'm afraid. You can't use that in test environments anyway since it doesn't exist.`}</p>
    <h3 {...{
      "id": "types"
    }}>{`types`}</h3>
    <p>{`Using TypeScript in tests, you'll have to jump through another hoop: TypeScript doesn't understand that isomorphic-fetch is mocked and gets confused.`}</p>
    <p>{`You have to tell the type system that `}<em parentName="p">{`"Hey, this is actually fetch-mock. All is well."`}</em></p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=null&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20src%2F__tests__%2FAuthHelper.spec.ts%0A%0Aimport%20_fetchMock%20from%20'isomorphic-fetch'%20%2F%2F%20imports%20the%20sandbox%0Atype%20FetchMock%20%3D%20import('fetch-mock')%20%2F%2F%20imports%20type%20info%0A%0Aconst%20fetchMock%20%3D%20_fetchMock%20as%20FetchMock%20%2F%2F%20casts%20as%20correct%20type"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "630px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "43.946188340807176%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'176\\'%20viewBox=\\'0%200%20400%20176\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M21%2033a1385%201385%200%20001%20112c2%202%20354%202%20356%200%202-1%202-6%202-56l-1-56c-1-3-357-3-358%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/dcefd936a3aed18223afcb472b4d194f/ca0a1/techletter.app-screenshot-1582908305261.webp 223w", "/static/dcefd936a3aed18223afcb472b4d194f/75680/techletter.app-screenshot-1582908305261.webp 445w", "/static/dcefd936a3aed18223afcb472b4d194f/8aab1/techletter.app-screenshot-1582908305261.webp 630w"],
                "sizes": "(max-width: 630px) 100vw, 630px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/dcefd936a3aed18223afcb472b4d194f/e92b6/techletter.app-screenshot-1582908305261.png 223w", "/static/dcefd936a3aed18223afcb472b4d194f/e66bf/techletter.app-screenshot-1582908305261.png 445w", "/static/dcefd936a3aed18223afcb472b4d194f/f058b/techletter.app-screenshot-1582908305261.png 630w"],
                "sizes": "(max-width: 630px) 100vw, 630px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/dcefd936a3aed18223afcb472b4d194f/f058b/techletter.app-screenshot-1582908305261.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Convoluted but how else is TypeScript supposed to know `}<inlineCode parentName="p">{`isomorphic-fetch`}</inlineCode>{` is actually `}<inlineCode parentName="p">{`fetch-mock`}</inlineCode>{` ...`}</p>
    <p>{`PS: I'm assuming Jest because it's become the industry standard for JavaScript testing in the past few years.`}</p>
    <h2 {...{
      "id": "and-now-it-works-️"
    }}>{`And now it works ✌️`}</h2>
    <p>{`All you gotta do now is mock a request and write your test.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20src%2F__tests__%2FAuthHelper.spec.ts%0A%0Adescribe(%22isAuthenticated%22%2C%20()%20%3D%3E%20%7B%0A%09%2F%2F%20resets%20your%20mocks%20so%20tests%20don't%20bleed%20into%20each%20other%0A%09afterEach(()%20%3D%3E%20%7B%0A%20%20%20%20fetchMock.restore()%3B%0A%20%20%20%20fetchMock.reset()%3B%0A%09%7D)%3B%0A%09%0A%09it(%22is%20true%20on%20server%20success%22%2C%20async%20()%20%3D%3E%20%7B%0A%09%09fetchMock.mock(%22http%3A%2F%2Flocalhost%3A3000%2Fapi%2Fuser%2Fauthorize%22%2C%20%7B%0A%09%09%09status%3A%20200%2C%0A%09%09%09body%3A%20%7B%20success%3A%20true%20%7D%0A%09%09%7D)%3B%0A%09%09%0A%09%09expect(await%20AuthHelper.isAuthenticated()).toBe(true)%3B%0A%09%7D)%3B%0A%09%0A%E2%80%8C%09it(%22is%20false%20on%20server%20unauthenticated%22%2C%20async%20()%20%3D%3E%20%7B%0A%09%09fetchMock.mock(%22http%3A%2F%2Flocalhost%3A3000%2Fapi%2Fuser%2Fauthorize%22%2C%20%7B%0A%09%09%09status%3A%20401%2C%0A%09%09%09body%3A%20%7B%20success%3A%20false%20%7D%0A%09%09%7D)%3B%0A%09%09%0A%09%09expect(await%20AuthHelper.isAuthenticated()).toBe(false)%3B%0A%09%7D)%3B%0A%7D)"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "646px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "101.34529147982063%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'406\\'%20viewBox=\\'0%200%20400%20406\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M21%2031a11315%2011315%200%20001%20345h357l1-173V31l-180-1-179%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/21a0a3f6dea6b0fb50a2129495976553/ca0a1/techletter.app-screenshot-1582908305311.webp 223w", "/static/21a0a3f6dea6b0fb50a2129495976553/75680/techletter.app-screenshot-1582908305311.webp 445w", "/static/21a0a3f6dea6b0fb50a2129495976553/8c2f2/techletter.app-screenshot-1582908305311.webp 646w"],
                "sizes": "(max-width: 646px) 100vw, 646px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/21a0a3f6dea6b0fb50a2129495976553/e92b6/techletter.app-screenshot-1582908305311.png 223w", "/static/21a0a3f6dea6b0fb50a2129495976553/e66bf/techletter.app-screenshot-1582908305311.png 445w", "/static/21a0a3f6dea6b0fb50a2129495976553/27524/techletter.app-screenshot-1582908305311.png 646w"],
                "sizes": "(max-width: 646px) 100vw, 646px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/21a0a3f6dea6b0fb50a2129495976553/27524/techletter.app-screenshot-1582908305311.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Each of those tests is saying `}<em parentName="p">{`"When you fetch() this URL, return this object`}</em>{`. No network calls are made, no servers harmed, no ambiguity about what happens. Just solid tests doing exactly what you want.`}</p>
    <p>{`And now you know your code works. Bliss 😌`}</p>
    <p>{`Happy Friday,`}<br parentName="p"></br>{`
`}{`~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      